import React, { useState, useRef, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
const PAGE_SIZE = 50;

// Create worker script as a string
const workerScript = `
self.onmessage = function (e) {
    const { chunk, chunkIndex } = e.data;

    try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(chunk, "text/xml");
        const items = xmlDoc.getElementsByTagName('previous');

        const parsedItems = Array.from(items).map(item => {
            const tpp = item.getElementsByTagName('tpp')[0];
            const mpp = item.getElementsByTagName('mpp')[0];

            if (tpp) {
                return {
                    id: item.getAttribute('xml:id'),
                    type: 'TPP',
                    code: tpp.getElementsByTagName('code')[0]?.textContent || '',
                    term: tpp.getElementsByTagName('preferred-term')[0]?.textContent || '',
                    packSize: tpp.getElementsByTagName('pack-size')[0]?.textContent || '',
                    brandName: tpp.getElementsByTagName('brand-name')[0]?.getElementsByTagName('value')[0]?.textContent || '',
                    price: tpp.getElementsByTagName('ex-manufacturer')[0]?.getElementsByTagName('amount')[0]?.textContent || '',
                };
            } else if (mpp) {
                return {
                    id: item.getAttribute('xml:id'),
                    type: 'MPP',
                    code: mpp.getElementsByTagName('code')[0]?.textContent || '',
                    term: mpp.getElementsByTagName('preferred-term')[0]?.textContent || '',
                    packSize: mpp.getElementsByTagName('pack-size')[0]?.textContent || '',
                };
            }
            return null;
        }).filter(Boolean);

        self.postMessage({ parsedItems, chunkIndex });
    } catch (error) {
        self.postMessage({ error: error.message, chunkIndex });
    }
};
`;

const UploadIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-8 h-8 mb-2 text-gray-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
        />
    </svg>
);

const steps = [
    { id: 1, name: 'Upload', description: 'Upload PBS XML file' },
    { id: 2, name: 'Preview', description: 'Review data' },
    { id: 3, name: 'Import', description: 'Import to system' }
];

const PBSImport = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [xmlData, setXmlData] = useState([]);
    const [error, setError] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [importProgress, setImportProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [processedChunks, setProcessedChunks] = useState(new Set());
    const [workers, setWorkers] = useState([]);

    const parentRef = useRef(null);
    const rowVirtualizer = useVirtualizer({
        count: totalItems,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 35,
        overscan: 5,
    });

    useEffect(() => {
        return () => {
            workers.forEach(worker => worker.terminate());
        };
    }, [workers]);

    const createWorker = () => {
        const blob = new Blob([workerScript], { type: 'application/javascript' });
        const workerUrl = URL.createObjectURL(blob);
        const worker = new Worker(workerUrl);
        URL.revokeObjectURL(workerUrl); // Clean up the URL
        return worker;
    };

    const processXMLChunk = async (chunk, chunkIndex, worker) => {
        return new Promise((resolve, reject) => {
            worker.onmessage = (e) => {
                if (e.data.error) {
                    reject(new Error(e.data.error));
                } else {
                    resolve(e.data);
                }
            };
            worker.postMessage({ chunk, chunkIndex });
        });
    };

    const parseXMLData = async (file) => {
        try {
            setIsUploading(true);
            const fileSize = file.size;
            const chunks = Math.ceil(fileSize / CHUNK_SIZE);
            const newWorkers = [];

            // Create workers
            const workerCount = Math.min(4, navigator.hardwareConcurrency || 2);
            for (let i = 0; i < workerCount; i++) {
                const worker = createWorker();
                newWorkers.push(worker);
            }
            setWorkers(newWorkers);

            let processedItems = [];
            let currentChunk = 0;

            while (currentChunk < chunks) {
                const chunkPromises = newWorkers.map(async (worker, index) => {
                    const chunk = currentChunk + index;
                    if (chunk >= chunks) return null;

                    const start = chunk * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, fileSize);
                    const fileSlice = file.slice(start, end);
                    const chunkText = await fileSlice.text();

                    try {
                        const { parsedItems } = await processXMLChunk(chunkText, chunk, worker);
                        setProcessedChunks(prev => new Set([...prev, chunk]));
                        return parsedItems;
                    } catch (error) {
                        console.error(`Error processing chunk ${chunk}:`, error);
                        return [];
                    }
                });

                const chunkResults = await Promise.all(chunkPromises);
                processedItems = [...processedItems, ...chunkResults.flat().filter(Boolean)];

                setUploadProgress((currentChunk / chunks) * 100);
                setXmlData(prev => [...prev, ...processedItems]);
                setTotalItems(prev => prev + processedItems.length);

                currentChunk += workerCount;
            }

            newWorkers.forEach(worker => worker.terminate());
            setWorkers([]);

            setCurrentStep(2);
            setIsUploading(false);
            setUploadProgress(100);
        } catch (err) {
            setError('Error parsing XML file. Please ensure it is a valid PBS XML format.');
            console.error('Parsing error:', err);
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setError(null);
            setUploadProgress(0);
            setXmlData([]);
            setTotalItems(0);
            setProcessedChunks(new Set());
        }
    };

    const handleUpload = () => {
        if (!selectedFile) return;
        parseXMLData(selectedFile);
    };

    const handleImport = () => {
        setIsImporting(true);
        setImportProgress(0);

        const totalSteps = 100;
        let currentStep = 0;

        const importInterval = setInterval(() => {
            if (currentStep < totalSteps) {
                currentStep += 1;
                setImportProgress(currentStep);
            } else {
                clearInterval(importInterval);
                setIsImporting(false);
                setCurrentStep(3);
            }
        }, 50);
    };

    const handleCancel = () => {
        setSelectedFile(null);
        setUploadProgress(0);
        setImportProgress(0);
        setIsUploading(false);
        setIsImporting(false);
        setError(null);
        setCurrentStep(1);
        setXmlData([]);
        setTotalItems(0);
        setProcessedChunks(new Set());

        workers.forEach(worker => worker.terminate());
        setWorkers([]);
    };

    const virtualRows = rowVirtualizer.getVirtualItems();

    return (
        <div className="w-full">
            <ul className="steps steps-horizontal w-full mb-8">
                {steps.map((step) => (
                    <li
                        key={step.id}
                        className={`step ${currentStep >= step.id ? 'step-primary' : ''}`}
                        data-content={currentStep > step.id ? '✓' : step.id}
                    >
                        {step.name}
                    </li>
                ))}
            </ul>

            <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                    {currentStep === 1 && (
                        <>
                            <h2 className="card-title">Upload PBS File</h2>
                            <p className="text-base-content/70">Select and upload your PBS XML data file</p>

                            <div className="my-6">
                                <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-base-200 hover:bg-base-300 ${isUploading ? 'pointer-events-none opacity-50' : ''}`}>
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <UploadIcon />
                                        <p className="mb-2 text-sm">
                                            <span className="font-semibold">Click to upload</span> or drag and drop
                                        </p>
                                        <p className="text-xs opacity-70">PBS XML file</p>
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept=".xml"
                                        onChange={handleFileSelect}
                                        disabled={isUploading}
                                    />
                                </label>

                                {selectedFile && (
                                    <div className="mt-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm">{selectedFile.name}</span>
                                            <div className="flex gap-2">
                                                {!isUploading ? (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            onClick={handleUpload}
                                                        >
                                                            Upload
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-ghost"
                                                            onClick={handleCancel}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        className="btn btn-sm btn-ghost"
                                                        onClick={handleCancel}
                                                    >
                                                        Cancel Upload
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {(isUploading || uploadProgress > 0) && (
                                            <div className="w-full">
                                                <progress
                                                    className="progress progress-primary w-full"
                                                    value={uploadProgress}
                                                    max="100"
                                                ></progress>
                                                <div className="text-sm text-center mt-1">
                                                    {uploadProgress < 100 ? 'Processing chunks...' : 'Finalizing...'}
                                                    {Math.round(uploadProgress)}% ({processedChunks.size} chunks processed)
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {currentStep === 2 && xmlData.length > 0 && (
                        <>
                            <h2 className="card-title">Preview Data</h2>
                            <p className="text-base-content/70">Review the data before importing ({totalItems.toLocaleString()} items)</p>

                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center gap-2">
                                    <span>Show</span>
                                    <select
                                        className="select select-bordered"
                                        value={PAGE_SIZE}
                                        disabled
                                    >
                                        <option value={50}>50</option>
                                    </select>
                                    <span>entries per page</span>
                                </div>
                                <div className="text-sm">
                                    Showing {Math.min(PAGE_SIZE, totalItems)} of {totalItems.toLocaleString()} entries
                                </div>
                            </div>

                            <div
                                ref={parentRef}
                                className="overflow-auto h-96"
                            >
                                <div
                                    style={{
                                        height: `${rowVirtualizer.getTotalSize()}px`,
                                        width: '100%',
                                        position: 'relative',
                                    }}
                                >
                                    <table className="table table-zebra w-full">
                                        <thead className="sticky top-0 bg-base-100 z-10">
                                            <tr>
                                                <th>ID</th>
                                                <th>Type</th>
                                                <th>Code</th>
                                                <th>Term</th>
                                                <th>Pack Size</th>
                                                <th>Brand Name</th>
                                                <th>Price</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {virtualRows.map(virtualRow => {
                                                const item = xmlData[virtualRow.index];
                                                return (
                                                    <tr
                                                        key={virtualRow.index}
                                                        className="hover"
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: `${virtualRow.size}px`,
                                                            transform: `translateY(${virtualRow.start}px)`,
                                                        }}
                                                    >
                                                        <td className="font-medium">{item.id}</td>
                                                        <td>{item.type}</td>
                                                        <td>{item.code}</td>
                                                        <td>{item.term}</td>
                                                        <td>{item.packSize}</td>
                                                        <td>{item.brandName || '-'}</td>
                                                        <td>{item.price || '-'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="flex justify-between items-center mt-4">
                                <div className="text-sm">
                                    Virtual scrolling enabled for {totalItems.toLocaleString()} records
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        className="btn btn-sm btn-ghost"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-sm btn-primary"
                                        onClick={handleImport}
                                        disabled={isImporting}
                                    >
                                        Import Data
                                    </button>
                                </div>
                            </div>

                            {isImporting && (
                                <div className="mt-4">
                                    <progress
                                        className="progress progress-primary w-full"
                                        value={importProgress}
                                        max="100"
                                    ></progress>
                                    <div className="text-sm text-center mt-1">
                                        Importing {totalItems.toLocaleString()} records... {Math.round(importProgress)}%
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {currentStep === 3 && (
                        <div className="text-center py-8">
                            <svg
                                className="mx-auto h-12 w-12 text-success"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                            <h3 className="mt-2 text-lg font-medium">Import Complete</h3>
                            <p className="mt-1 text-sm text-base-content/70">
                                Successfully imported {totalItems.toLocaleString()} PBS records
                            </p>
                            <div className="mt-6">
                                <button
                                    className="btn btn-primary"
                                    onClick={handleCancel}
                                >
                                    Import Another File
                                </button>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-error mt-6">
                            <span>{error}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PBSImport;